import React, { useState, useMemo, useEffect, memo } from "react";
import { useGlobalData } from "../hooks/useGlobalHooks";
import { OptimizedNumberCell, LiveLTPCell, LivePNLCell, LiveAggregatedPNLCell } from "./OptimizedCell";
import { Pencil } from "lucide-react";
import BulkModifyModal from "./BulkModifyModal";
import { toast } from "react-hot-toast";

/**
 * Position Comparison Modal
 * Compares actual client positions with reference base quantities
 * Shows Symbol, Strategy, and Qty separately
 */
export function PositionComparisonModal({
  isOpen,
  onClose,
  clientData,
  orders,
  positions,
  symbolStrategyBaseQty,
  onCancel,
}) {
  const { users, indexMultipliers } = useGlobalData();

  // Selection & Modify State
  const [selectedOrderIds, setSelectedOrderIds] = useState(new Set());
  const [modifyModalOpen, setModifyModalOpen] = useState(false);
  const [ordersToModify, setOrdersToModify] = useState([]);

  // console.log("🔍 Modal Debug:", {
  //   isOpen,
  //   clientData,
  //   positionsCount: positions?.length,
  //   usersCount: users?.length,
  //   indexMultipliersCount: indexMultipliers?.length,
  //   symbolStrategyBaseQtyCount: Object.keys(symbolStrategyBaseQty || {}).length,
  //   samplePosition: positions?.[0],
  //   clientIdToMatch: clientData?.clientId,
  // });

  // Get all indexes this client has positions in
  const clientIndexes = useMemo(() => {
    if (!positions || !clientData?.clientId) {
      // console.log("❌ Missing data:", {
      //   hasPositions: !!positions,
      //   hasClientData: !!clientData,
      //   hasClientId: !!clientData?.clientId,
      // });
      return [];
    }


    const indexes = new Set();
    const matchingPositions = [];

    positions.forEach((pos) => {
      if (pos.client_id === clientData.clientId) {
        matchingPositions.push(pos);
        // Extract index from symbol or use the index field
        const index = pos.index || extractIndexFromSymbol(pos.symbol);
        if (index) {
          indexes.add(index.toLowerCase());
        }
      }
    });


    return Array.from(indexes);
  }, [positions, clientData]);

  // Extract index from symbol (e.g., "NSE:NIFTY25NOV26000CE" -> "nifty")
  function extractIndexFromSymbol(symbol) {
    if (!symbol) return null;

    const symbolUpper = symbol.toUpperCase();

    // EXCLUDE MCX, NFO, BFO, and FUT symbols
    if (symbolUpper.includes("MCX") || symbolUpper.includes("NFO") || symbolUpper.includes("BFO") || symbolUpper.endsWith("FUT")) {
      return null;
    }

    if (symbolUpper.includes("NIFTY") && symbolUpper.includes("BANK")) {
      return "banknifty";
    } else if (
      symbolUpper.includes("FINNIFTY") ||
      symbolUpper.includes("FIN NIFTY")
    ) {
      return "finnifty";
    } else if (
      symbolUpper.includes("MIDCPNIFTY") ||
      symbolUpper.includes("MIDCP")
    ) {
      return "midcpnifty";
    } else if (symbolUpper.includes("NIFTY")) {
      return "nifty";
    } else if (symbolUpper.includes("SENSEX") || symbolUpper.includes("BSX")) {
      return "sensex";
    } else if (symbolUpper.includes("BANKEX")) {
      return "bankex";
    } else {
      return null; // Ignore non-index symbols
    }
  }

  /**
   * Format index name for display (handles Strike 1/2 logic)
   * @param {string} index - Index name (e.g. nifty, nifty_strike_2)
   * @param {Array} allIndexes - List of all active index keys
   */
  function formatIndexLabel(index, allIndexes = []) {
    if (!index) return "-";
    const parts = index.toUpperCase().split('_');
    const base = parts[0];
    
    // Check if any other index has the same base but is a different strike leg
    const hasMultipleStrikes = allIndexes.some(i => {
      const iUp = i.toUpperCase();
      return iUp.startsWith(`${base}_`) && iUp !== base;
    });

    if (!hasMultipleStrikes && parts.length === 1) return base;
    if (parts.length === 1) return `${base} STRIKE 1`;
    
    return parts.join(' ').replace('STRIKE', 'STRIKE'); // Already uppercase
  }

  // Auto-select 1x reference clients for each index
  const referenceClients = useMemo(() => {
    if (!positions || clientIndexes.length === 0) {
      // console.log("⚠️ Missing data for reference clients:", {
      //   hasPositions: !!positions,
      //   indexesCount: clientIndexes.length,
      // });
      return {};
    }

    // FIXED reference client IDs
    const REFERENCE_CLIENT_IDS = {
      nifty: "FAB15958", // Ajay Dalmia
      banknifty: "CA00246", // Clearmind Consultancy
      finnifty: "CA00246", // Clearmind Consultancy
      midcpnifty: "CA00246", // Clearmind Consultancy
      sensex: "CA00246", // Clearmind Consultancy
    };

    const references = {};

    clientIndexes.forEach((index) => {
      // Only process index positions (ignore stocks)
      if (!index) {
        return;
      }

      const refClientId = REFERENCE_CLIENT_IDS[index];

      if (refClientId) {
        // Check if this client has positions for this index
        const hasPositions = positions.some((p) => {
          const posIndex = (
            p.index || extractIndexFromSymbol(p.symbol)
          )?.toLowerCase();
          return p.client_id === refClientId && posIndex === index;
        });

      } else {
        // console.warn(`⚠️ No reference client configured for ${index}`);
      }
    });

    return references;
  }, [positions, clientIndexes]);

  // Group positions by symbol+strategy for comparison
  const positionsBySymbolStrategy = useMemo(() => {
    if (!positions || !clientData?.clientId || !symbolStrategyBaseQty)
      return [];

    // Track all matched order IDs to find orphans later
    const matchedOrderIds = new Set();

    // console.log(
    //   "📊 Building position comparison with symbolStrategyBaseQty:",
    //   symbolStrategyBaseQty
    // );

    const rows = [];
    const processedKeys = new Set();
    const clientId = clientData.clientId;

    // Get all positions for this client
    const clientPositions = positions.filter(
      (p) => p.client_id === clientId
    );



    // Process each position
    clientPositions.forEach((pos) => {
      const key = `${pos.symbol}_${pos.strategy || "NO_STRATEGY"}`;

      // Skip if already processed
      if (processedKeys.has(key)) return;
      processedKeys.add(key);

      // Get base qty info for this symbol+strategy
      const baseQtyInfo = symbolStrategyBaseQty[key];

      if (!baseQtyInfo) {
        console.warn(`⚠️ No base qty found for ${key}`);
        return;
      }

      // Get multiplier for this client and index
      const index = baseQtyInfo.index?.toUpperCase();
      const multiplier = getMultiplier(index);

      // Calculate expected qty
      const baseQty = baseQtyInfo.baseQty;
      const expectedQty = multiplier * baseQty;

      // Sum actual qty for this client's symbol+strategy
      let actualQty = 0;
      clientPositions.forEach((p) => {
        const pKey = `${p.symbol}_${p.strategy || "NO_STRATEGY"}`;
        if (pKey === key) {
          actualQty += Math.abs(parseFloat(p.net_qty || p.qty || 0));
        }
      });

      // Find matching Pending Sell Orders (ALL matches)
      let matchingOrders = [];
      if (orders) {
        matchingOrders = orders.filter(o => {
          const oClientId = o.clientId || o.client_id;
          const oSymbol = o.symbol;
          const oStrategy = o.strategy;
          const oSide = String(o.side).toLowerCase();
          const oStatus = (o.status || "").toLowerCase();
          const oStatusNo = o.status_no !== undefined ? parseInt(o.status_no, 10) : undefined;

          // Client, Symbol, Strategy Match

          const currentClientId = (oClientId || "").toString().trim();
          const targetClientId = (clientId || "").toString().trim();
          if (currentClientId !== targetClientId) return false;

          // Normalize symbols (handle MCX: prefix if any, though less likely for index)
          const cleanPosSymbol = (pos.symbol || "").replace(/^MCX:/i, "").trim();
          const cleanOrderSymbol = (oSymbol || "").replace(/^MCX:/i, "").trim();

          // Strategy Match (Case-insensitive)
          const currentStrategy = (oStrategy || "").toString().trim().toUpperCase();
          const targetStrategy = (pos.strategy || "").toString().trim().toUpperCase();

          if (cleanPosSymbol !== cleanOrderSymbol) return false;
          if (currentStrategy !== targetStrategy) return false;

          // Side Match (Sell: -1, 2, "sell")
          const isSell = oSide === "-1" || oSide === "2" || oSide === "sell";
          if (!isSell) return false;

          // Status Match (Strict: Pending AND 6)
          const isPending = oStatus === "pending" && oStatusNo === 6;
          return isPending;
        });
      }

      // Calculate total SL Qty from all matching orders
      const totalSlQty = matchingOrders.reduce((acc, o) => acc + (parseInt(o.qty) || 0), 0);

      // Track matched IDs
      matchingOrders.forEach(o => matchedOrderIds.add(o.orderid || o.order_id));

      // Calculate 1x Position Value to check <= 50k threshold
      const buyPrice = pos.net_avg || pos.avg_price || pos.buy_price || pos.buy_avg_price || pos.price || 0;
      const mul = Number(multiplier) > 0 ? Number(multiplier) : 1;
      const posValue1x = (actualQty / mul) * Number(buyPrice || 0);
      const isValueExceeded = posValue1x > 50000;

      rows.push({
        symbol: pos.symbol,
        strategy: pos.strategy || "N/A",
        index: baseQtyInfo.index,
        baseQty: baseQty,
        multiplier: multiplier,
        expectedQty: expectedQty,
        actualQty: actualQty,
        buyPrice: buyPrice,
        posValue1x: posValue1x,
        isValueExceeded: isValueExceeded,
        match: actualQty === expectedQty,
        clientsTrading: baseQtyInfo.clients,
        strike: extractStrike(pos.symbol),
        matchingPositions: [pos],
        matchingOrders: matchingOrders,
        totalSlQty: totalSlQty
      });
    });

    // --- ADD ORPHAN ORDERS TO ROWS ---
    if (orders) {
      orders.forEach(o => {
        const oClientId = (o.clientId || o.client_id || "").toString().trim();
        const oSide = String(o.side).toLowerCase();
        const oStatus = (o.status || "").toLowerCase();
        const oStatusNo = o.status_no !== undefined ? parseInt(o.status_no, 10) : undefined;
        const targetClientId = (clientData.clientId || "").toString().trim();

        // Must belong to this client
        if (oClientId !== targetClientId) return;

        const isSell = oSide === "-1" || oSide === "2" || oSide === "sell";
        const isPending = oStatus === "pending" && oStatusNo === 6;

        // Check if already matched
        const orderId = o.orderid || o.order_id;
        const isMatched = matchedOrderIds.has(orderId);

        // EXTRA CHECK: Must be an index symbol (ignore MCX, FUT, NFO, etc.)
        const indexResult = extractIndexFromSymbol(o.symbol);

        if (isSell && isPending && !isMatched && indexResult) {
          // Determine Index from Symbol if possible (optional, purely for display)
          // We leave it null or try to extract

          rows.push({
            symbol: o.symbol,
            strategy: o.strategy || "Unknown",
            index: indexResult, // No position context
            baseQty: 0,
            multiplier: null,
            expectedQty: 0,
            actualQty: 0,
            buyPrice: 0,
            posValue1x: 0,
            isValueExceeded: false,
            match: false,
            clientsTrading: [],
            strike: extractStrike(o.symbol),
            matchingOrders: [o], // The orphan itself
            totalSlQty: parseInt(o.qty) || 0,
            isOrphan: true // Flag to style differently
          });
        }
      });
    }

    // console.log("✅ Position comparison rows (including orphans):", rows.length, rows);

    return { rows, matchedOrderIds };
  }, [positions, clientData, symbolStrategyBaseQty, orders]); // Added orders dependency

  // -- FILTER LOGIC --
  const [selectedAsset, setSelectedAsset] = useState("ALL");

  const uniqueAssets = useMemo(() => {
    const rows = positionsBySymbolStrategy.rows || [];
    const assets = new Set(rows.map(r => r.index).filter(Boolean));
    return ["ALL", ...Array.from(assets).sort()];
  }, [positionsBySymbolStrategy]);

  const filteredRows = useMemo(() => {
    const rows = positionsBySymbolStrategy.rows || [];
    if (selectedAsset === "ALL") return rows;
    return rows.filter(r => r.index === selectedAsset);
  }, [positionsBySymbolStrategy, selectedAsset]);



  // Format time (HH:MM:SS)
  function formatTime(isoString) {
    if (!isoString) return "-";
    try {
      const date = new Date(isoString);
      return date.toLocaleTimeString('en-IN', { hour12: false });
    } catch (e) {
      return isoString;
    }
  }

  // Extract strike from symbol
  function extractStrike(symbol) {
    const match = symbol?.match(/(\d{5,6})(CE|PE)$/);
    return match ? match[1] : "N/A";
  }

  // Format price with commas and 2 decimals
  function formatPrice(num) {
    if (num === undefined || num === null || num === 0) return "-";
    return Number(num).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  // Get multiplier for an index
  function getMultiplier(index) {
    if (!clientData?.clientId) {
      return "N/A";
    }

    // Check if client is multi-asset
    const matchedUser = users?.find(u => (u.client_id || u.clientId)?.toString().trim() === clientData.clientId?.toString().trim());
    const isMultiAsset = clientData.isMultiAsset || (matchedUser?.product || "").toLowerCase().includes("multi");
    if (isMultiAsset) {
      const maMul = clientData.multi_asset_multiplier > 0 
        ? clientData.multi_asset_multiplier 
        : (clientData.effectiveMaMul || Number(matchedUser?.multi_asset_multiplier ?? matchedUser?.multiasset_multiplier ?? matchedUser?.multiplier) || 1);
      return maMul;
    }

    if (!indexMultipliers) {
      return "N/A";
    }

    // Find all multipliers for this client and index
    const matches = indexMultipliers.filter(
      (m) => m.client_id === clientData.clientId && m.index === index
    );

    // Prioritize Dhan XTS if available
    const mult =
      matches.find((m) => ["dhan_xts", "dhan_xts_anurag"].includes((m.broker || "").toLowerCase())) ||
      matches[0];

    return mult ? parseFloat(mult.multiplier) : "N/A";
  }

  // Open Confirmation Modal via Parent
  const openCancelModal = (orders) => {
    if (onCancel) {
      onCancel(orders);
    }
  };

  // Modify Handlers
  const openModifyModal = (orderList) => {
    // Enrich orderList with expectedQty if available from row context
    const enrichedOrders = orderList.map(o => {
      // Find row matching this order
      const row = filteredRows.find(r => r.matchingOrders?.some(mo => (mo.order_id || mo.orderId) === (o.order_id || o.orderId)));
      return {
        ...o,
        expectedQty: row?.expectedQty
      };
    });
    setOrdersToModify(enrichedOrders);
    setModifyModalOpen(true);
  };

  const toggleOrderSelection = (orderId) => {
    const next = new Set(selectedOrderIds);
    if (next.has(orderId)) next.delete(orderId);
    else next.add(orderId);
    setSelectedOrderIds(next);
  };

  const handleBulkModify = () => {
    const selectedOrders = [];
    filteredRows.forEach(row => {
      if (row.matchingOrders) {
        row.matchingOrders.forEach(o => {
          if (selectedOrderIds.has(o.order_id || o.orderId)) {
            selectedOrders.push(o);
          }
        });
      }
    });

    if (selectedOrders.length === 0) {
      toast.error("Please select at least one order to modify");
      return;
    }
    openModifyModal(selectedOrders);
  };

  if (!isOpen) return null;

  return (
    <>


      <div
        className="fixed inset-0 bg-black/20 flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <div
          className="bg-[#1a1d29] rounded-lg shadow-xl max-w-[90vw] w-full max-h-[80vh] overflow-hidden flex flex-col border border-gray-700/50"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-[#14161f] px-6 py-4 border-b border-gray-700 flex justify-between items-center">
            <div className="flex items-center gap-4">
              <h2 className="text-xl font-bold text-white">Position Comparison</h2>
              {selectedOrderIds.size > 0 && (
                <button
                  onClick={handleBulkModify}
                  className="bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold px-3 py-1.5 rounded transition-all shadow-lg flex items-center gap-2"
                >
                  Modify Selected ({selectedOrderIds.size})
                </button>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Client Info */}
          <div className="bg-[#1a1d29] px-6 py-3 border-b border-gray-700/50">
            <div className="flex items-center gap-4 text-sm">
              <span className="text-gray-400">Client:</span>
              <span className="text-white font-semibold">
                {clientData?.clientName || clientData?.clientId}
              </span>
              <span className="text-gray-400">({clientData?.clientId})</span>
            </div>
          </div>

          {/* High 1x Value Warning Banner */}
          {filteredRows?.some(r => !r.isOrphan && r.isValueExceeded) && (
            <div className="bg-red-900/30 border-b border-red-500/40 px-6 py-2.5 flex items-center justify-between text-xs text-red-300">
              <div className="flex items-center gap-2">
                <span className="font-bold text-sm">⚠️ High 1x Position Value Alert:</span>
                <span>One or more positions exceed the ₹50,000 threshold at 1x multiplier.</span>
              </div>
              <span className="font-mono bg-red-800/50 px-2 py-0.5 rounded text-red-200 border border-red-600/40">
                Limit: ₹50,000 / 1x
              </span>
            </div>
          )}

          {/* Debug Info Bar */}
          {/* <div className="bg-blue-900/20 px-6 py-2 border-b border-blue-700/30 text-xs">
          <div className="flex gap-6 text-gray-300">
            <span>📊 Total Positions: {positions?.length || 0}</span>
            <span>👥 Users: {users?.length || 0}</span>
            <span>📈 Index Multipliers: {indexMultipliers?.length || 0}</span>
            <span>📊 Total Positions: {positions?.length || 0}</span>
            <span>👤 Users: {users?.length || 0}</span>
            <span> Index Multipliers: {indexMultipliers?.length || 0}</span>
            <span>📝 Rows: {positionsBySymbolStrategy.length}</span>
          </div>
        </div> */}

          {/* Content */}
          <div className="flex-1 overflow-auto">
            {positionsBySymbolStrategy.rows?.length === 0 ? (
              <div className="text-center py-8 px-6">
                <p className="text-gray-400 mb-4">
                  No positions found for comparison
                </p>
                <div className="text-left bg-red-900/20 border border-red-700/30 rounded p-4 max-w-2xl mx-auto text-xs">
                  <p className="text-red-400 font-semibold mb-2">Debug Info:</p>
                  <p className="text-gray-300">
                    • Total positions available: {positions?.length || 0}
                  </p>
                  <p className="text-gray-300">
                    • Client ID: {clientData?.clientId || "N/A"}
                  </p>
                  <p className="text-gray-300">
                    • Client Name: {clientData?.clientName || "N/A"}
                  </p>
                  <p className="text-gray-300">
                    • Symbol+Strategy base qty entries:{" "}
                    {Object.keys(symbolStrategyBaseQty || {}).length}
                  </p>
                  <p className="text-yellow-400 mt-2">
                    💡 Open browser console (F12) for detailed logs
                  </p>
                </div>
              </div>
            ) : (
              <div className="bg-[#14161f] overflow-hidden">
                <div className="overflow-auto max-h-[calc(80vh-280px)]">
                  <table className="w-full text-sm">
                    <thead className="bg-[#0f1117] sticky top-0 z-10">
                      <tr className="border-b border-gray-700/50">
                        <th colSpan="12" className="text-center py-2 bg-[#14161f] text-gray-400 font-semibold text-xs uppercase tracking-wider border-r border-gray-700/50">
                          Position Comparison
                        </th>
                        <th colSpan="7" className="text-center py-2 bg-red-900/10 text-red-400 font-semibold text-xs uppercase tracking-wider">
                          Matching Sell Orders
                        </th>
                      </tr>
                      <tr className="border-b border-gray-700/30">
                        <th className="w-8 px-2 text-center">
                          <input 
                            type="checkbox"
                            className="w-4 h-4 rounded border-gray-700 bg-gray-900 text-blue-600 focus:ring-blue-500"
                            checked={filteredRows.some(r => r.matchingOrders?.length > 0) && filteredRows.every(r => !r.matchingOrders || r.matchingOrders.every(o => selectedOrderIds.has(o.order_id || o.orderId)))}
                            onChange={(e) => {
                              const allIds = new Set(selectedOrderIds);
                              filteredRows.forEach(r => {
                                if (r.matchingOrders) {
                                  r.matchingOrders.forEach(o => {
                                    if (e.target.checked) allIds.add(o.order_id || o.orderId);
                                    else allIds.delete(o.order_id || o.orderId);
                                  });
                                }
                              });
                              setSelectedOrderIds(allIds);
                            }}
                          />
                        </th>
                        <th className="text-left py-3 px-4 text-gray-400 font-medium">
                          <div className="flex items-center gap-2">
                            <span>Index</span>
                            <select
                              value={selectedAsset}
                              onChange={(e) => setSelectedAsset(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              className="bg-[#1a1d29] border border-gray-700 text-gray-300 text-xs rounded px-1 py-0.5 focus:outline-none focus:border-blue-500"
                            >
                              {uniqueAssets.map(asset => {
                                if (asset === "ALL") return <option key="ALL" value="ALL">All</option>;
                                
                                const parts = asset.toUpperCase().split('_');
                                const base = parts[0];
                                const hasMultipleStrikes = uniqueAssets.some(a => a !== "ALL" && a.toUpperCase().startsWith(`${base}_`) && a.toUpperCase() !== base);
                                
                                let label = asset.toUpperCase();
                                if (hasMultipleStrikes || parts.length > 1) {
                                  if (parts.length === 1) label = `${base} STRIKE 1`;
                                  else label = `${base} ${parts.slice(1).join(' ').replace('STRIKE', 'STRIKE')}`;
                                }
                                
                                return (
                                  <option key={asset} value={asset}>
                                    {label}
                                  </option>
                                );
                              })}
                            </select>
                          </div>
                        </th>
                        <th className="text-left py-3 px-4 text-gray-400 font-medium">
                          Symbol
                        </th>
                        <th className="text-left py-3 px-4 text-gray-400 font-medium">
                          Strategy
                        </th>
                        <th className="text-center py-3 px-4 text-gray-400 font-medium">
                          Strike
                        </th>
                        <th className="text-center py-3 px-4 text-gray-400 font-medium">
                          Base Qty (1x)
                        </th>
                        <th className="text-center py-3 px-4 text-gray-400 font-medium">
                          Multiplier
                        </th>
                        <th className="text-center py-3 px-4 text-gray-400 font-medium">
                          Expected Qty
                        </th>
                        <th className="text-center py-3 px-4 text-gray-400 font-medium">
                          Actual Qty
                        </th>
                        <th className="text-center py-3 px-4 text-gray-400 font-medium whitespace-nowrap">
                          Buy Price
                        </th>
                        <th className="text-center py-3 px-4 text-gray-400 font-medium whitespace-nowrap">
                          Pos Value (1x)
                        </th>
                        <th className="text-center py-3 px-4 text-gray-400 font-medium">
                          LTP
                        </th>
                        <th className="text-center py-3 px-4 text-gray-400 font-medium border-r border-gray-700/50">
                          Match
                        </th>
                        <th className="text-center py-3 px-4 text-red-300 font-medium bg-red-900/5 border-l border-gray-700/30">
                          Order ID
                        </th>
                        <th className="text-center py-3 px-4 text-red-300 font-medium bg-red-900/5 border-l border-gray-700/30">
                          Time
                        </th>
                        <th className="text-center py-3 px-4 text-red-300 font-medium bg-red-900/5 border-l border-gray-700/30">
                          Order Qty
                        </th>
                        <th className="text-center py-3 px-4 text-red-300 font-medium bg-red-900/5 border-l border-gray-700/30">
                          Stop Price
                        </th>
                        <th className="text-center py-3 px-4 text-red-300 font-medium bg-red-900/5 border-l border-gray-700/30">
                          Limit Price
                        </th>
                        <th className="text-center py-3 px-4 text-red-300 font-medium bg-red-900/5 border-l border-gray-700/30">
                          Total Sell Qty
                        </th>
                        <th className="text-center py-3 px-4 text-red-300 font-medium bg-red-900/5 border-l border-gray-700/30">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRows?.map((row, idx) => {
                        const isMatch = row.actualQty === row.expectedQty;
                        const qtyColor = isMatch
                          ? "text-green-400"
                          : "text-red-400";
                        const hasOrders = row.matchingOrders && row.matchingOrders.length > 0;

                        return (
                          <tr
                            key={idx}
                            className="border-b border-gray-800/30 hover:bg-[#1a1d29] transition-colors valign-top"
                          >
                            <td className="px-2 text-center align-middle">
                              {hasOrders && (
                                <div className="flex flex-col gap-1 items-center">
                                  {row.matchingOrders.map((o, i) => (
                                    <div key={i} className="h-[20px] flex items-center">
                                      <input 
                                        type="checkbox"
                                        className="w-3.5 h-3.5 rounded border-gray-800 bg-gray-950 text-blue-600 focus:ring-blue-500"
                                        checked={selectedOrderIds.has(o.order_id || o.orderId)}
                                        onChange={() => toggleOrderSelection(o.order_id || o.orderId)}
                                      />
                                    </div>
                                  ))}
                                </div>
                              )}
                            </td>
                            <td className="py-2.5 px-4">
                              <span className="text-blue-400 text-xs font-semibold">
                                {formatIndexLabel(row.index, uniqueAssets)}
                              </span>
                            </td>
                            <td className="py-2.5 px-4 text-gray-300 text-xs font-mono">
                              {row.symbol}
                            </td>
                            <td className="py-2.5 px-4 text-gray-400 text-xs">
                              {row.strategy}
                            </td>
                            <td className="py-2.5 px-4 text-center text-gray-300">
                              {row.strike}
                            </td>
                            <td className="py-2.5 px-4 text-center text-white font-medium">
                              {row.isOrphan ? "-" : row.baseQty}
                            </td>
                            <td className="py-2.5 px-4 text-center text-purple-400 font-medium">
                              {row.isOrphan ? "-" : (Number.isFinite(Number(row.multiplier)) ? `${row.multiplier}x` : "-")}
                            </td>
                            <td className="py-2.5 px-4 text-center text-yellow-400 font-medium">
                              {row.isOrphan ? "-" : (Number.isFinite(row.expectedQty) ? row.expectedQty : "-")}
                            </td>
                            <td
                              className={`py-2.5 px-4 text-center font-bold ${qtyColor}`}
                            >
                              {row.isOrphan ? "-" : row.actualQty}
                            </td>
                            <td className="py-2.5 px-4 text-center text-gray-300 font-medium">
                              {row.isOrphan ? "-" : formatPrice(row.buyPrice)}
                            </td>
                            <td className="py-2.5 px-4 text-center font-mono">
                              {row.isOrphan ? (
                                "-"
                              ) : (
                                <span className={row.isValueExceeded ? "text-red-400 font-bold" : "text-gray-300 font-medium"}>
                                  ₹{Math.round(row.posValue1x || 0).toLocaleString("en-IN")}
                                  {row.isValueExceeded && (
                                    <span
                                      className="ml-1 text-[10px] px-1 py-0.2 rounded bg-red-500/20 text-red-300 border border-red-500/40 font-sans"
                                      title={`1x Position Value exceeds ₹50,000 threshold! ((Qty ${row.actualQty} / Mul ${row.multiplier}) * Buy Price ${row.buyPrice})`}
                                    >
                                      ⚠️
                                    </span>
                                  )}
                                </span>
                              )}
                            </td>
                            <td className="py-2.5 px-4 text-center text-blue-400 font-medium">
                              <LiveLTPCell symbol={row.symbol} formatFn={formatPrice} />
                            </td>
                            <td className="py-2.5 px-4 text-center border-r border-gray-700/50">
                              {row.isOrphan ? (
                                <span className="text-yellow-500" title="Orphaned Order">⚠️</span>
                              ) : isMatch ? (
                                <span className="text-green-400">✓</span>
                              ) : (
                                <span className="text-red-400">✗</span>
                              )}
                            </td>
                            {/* Order Details (Mapped) */}
                            <td className="py-2.5 px-4 text-center text-gray-300 text-xs font-mono border-l border-gray-700/50 align-top">
                              {hasOrders ? (
                                <div className="flex flex-col gap-1">
                                  {row.matchingOrders.map((o, i) => (
                                    <div key={i} className="whitespace-nowrap flex items-center justify-center gap-2 text-[10px]">
                                      <span className={o.status === 6 ? "text-blue-300" : "text-gray-400"}>
                                        {o.order_id || o.orderId || "N/A"}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-gray-600">-</span>
                              )}
                            </td>
                            <td className="py-2.5 px-4 text-center text-gray-300 text-xs font-mono border-l border-gray-700/30 align-top">
                              {hasOrders ? (
                                <div className="flex flex-col gap-1">
                                  {row.matchingOrders.map((o, i) => (
                                    <div key={i} className="text-gray-400 text-[10px]">
                                      {formatTime(o.orderdatetime || o.order_datetime || o.orderDateTime || o.orderTime)}
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-gray-600">-</span>
                              )}
                            </td>
                            <td className="py-2.5 px-4 text-center text-gray-300 font-medium border-l border-gray-700/30 align-top">
                              {hasOrders ? (
                                <div className="flex flex-col gap-1">
                                  {row.matchingOrders.map((o, i) => (
                                    <div key={i} className="text-red-300">
                                      {o.qty}
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-gray-600">-</span>
                              )}
                            </td>
                            <td className="py-2.5 px-4 text-center text-gray-300 font-medium border-l border-gray-700/30 align-top">
                              {hasOrders ? (
                                <div className="flex flex-col gap-1">
                                  {row.matchingOrders.map((o, i) => (
                                    <div key={i}>
                                      {o.stopPrice || o.stop_price || "-"}
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-gray-600">-</span>
                              )}
                            </td>
                            <td className="py-2.5 px-4 text-center text-gray-300 font-medium border-l border-gray-700/30 align-top">
                              {hasOrders ? (
                                <div className="flex flex-col gap-1">
                                  {row.matchingOrders.map((o, i) => (
                                    <div key={i}>
                                      {o.price || o.limitPrice || o.limit_price || "-"}
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-gray-600">-</span>
                              )}
                            </td>
                            <td className="py-2.5 px-4 text-center text-red-300 font-bold font-mono border-l border-gray-700/50">
                              {row.totalSlQty}
                            </td>
                            <td className="py-2.5 px-4 text-center border-l border-gray-700/50 align-middle">
                              {hasOrders ? (
                                <div className="flex flex-row items-center justify-center gap-3">
                                  {/* Vertical Stack of Delete Buttons */}
                                  <div className="flex flex-col gap-1 items-center">
                                    {row.matchingOrders.map((o, i) => (
                                      <div key={i} className="h-[20px] flex items-center gap-1">
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            openModifyModal([o]);
                                          }}
                                          className="text-blue-500/70 hover:text-blue-400 hover:bg-blue-500/10 p-1.5 rounded transition-all group border border-transparent hover:border-blue-500/30"
                                          title={`Modify Order ${o.order_id || o.orderId}`}
                                        >
                                          <Pencil className="h-3.5 w-3.5" />
                                        </button>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            openCancelModal([o]);
                                          }}
                                          className="text-red-500/70 hover:text-red-400 hover:bg-red-500/10 p-1.5 rounded transition-all group border border-transparent hover:border-red-500/30"
                                          title={`Cancel Order ${o.order_id || o.orderId}`}
                                        >
                                          <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="h-4 w-4"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              strokeWidth={1.5}
                                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                            />
                                          </svg>
                                        </button>
                                      </div>
                                    ))}
                                  </div>

                                  {/* Centered Cancel All Button */}
                                  {row.matchingOrders.length > 1 && (
                                    <div className="flex flex-col gap-2">
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openCancelModal(row.matchingOrders);
                                        }}
                                        className="text-[9px] font-bold text-red-400 hover:text-white bg-red-950/30 hover:bg-red-600 border border-red-900/50 hover:border-red-500 px-2 py-1 rounded transition-all whitespace-nowrap shadow-sm"
                                        title="Cancel All Matching Orders"
                                      >
                                        Cancel All
                                      </button>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openModifyModal(row.matchingOrders);
                                        }}
                                        className="text-[9px] font-bold text-blue-400 hover:text-white bg-blue-950/30 hover:bg-blue-600 border border-blue-900/50 hover:border-blue-500 px-2 py-1 rounded transition-all whitespace-nowrap shadow-sm"
                                        title="Modify All Matching Orders"
                                      >
                                        Modify All
                                      </button>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <span className="text-gray-600">-</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}


          </div>
        </div>
      </div>

      <BulkModifyModal 
        isOpen={modifyModalOpen}
        onClose={() => setModifyModalOpen(false)}
        orders={ordersToModify}
        onSuccess={() => {
          setSelectedOrderIds(new Set());
          // The parent view will refresh data naturally if hooked to global state
          // but toast is nice
        }}
      />
    </>
  );
}
